/**
 * ADR-0016 — banner when inventory ledger deviations trip Storage Safe Mode.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Activity } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  isStorageSafeMode,
  getStorageSafeModeReason,
  STORAGE_SAFE_MODE_CHANGED,
  disableStorageSafeMode
} from '../infrastructure/storage/storageSafeMode';
import { INVENTORY_LEDGER_SAFE_MODE_REASON } from '../domain/inventory/detectors';
import { getCurrentBusinessId } from '../utils/sharedCore';
import '../styles/components/system-banners.css';

function isInventoryLedgerSafeMode(reason) {
  return String(reason || '').startsWith(INVENTORY_LEDGER_SAFE_MODE_REASON);
}

const InventoryLedgerSafeModeBanner = () => {
  const { t } = useLanguage();
  const ti = (key) => t(`inventoryLedgerSafeMode.${key}`);
  const [active, setActive] = useState(() =>
    isStorageSafeMode() && isInventoryLedgerSafeMode(getStorageSafeModeReason())
  );
  const [reason, setReason] = useState(() => getStorageSafeModeReason());
  const [scanning, setScanning] = useState(false);
  const [summary, setSummary] = useState(null);

  const sync = useCallback(() => {
    const r = getStorageSafeModeReason();
    const on = isStorageSafeMode() && isInventoryLedgerSafeMode(r);
    setActive(on);
    setReason(r);
  }, []);

  useEffect(() => {
    window.addEventListener(STORAGE_SAFE_MODE_CHANGED, sync);
    return () => window.removeEventListener(STORAGE_SAFE_MODE_CHANGED, sync);
  }, [sync]);

  const handleRescan = async () => {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    if (!businessId || scanning) return;
    setScanning(true);
    try {
      const { runInventoryLedgerHealthScan } = await import(
        '../domain/inventory/detectors'
      );
      const result = await runInventoryLedgerHealthScan({
        businessId,
        enableSafeMode: true,
        clearSafeModeWhenOk: true
      });
      setSummary({
        ok: result.ok,
        count: result.deviationCount,
        error: result.error || null
      });
      sync();
    } catch (err) {
      setSummary({ ok: false, count: 0, error: err?.message || String(err) });
    } finally {
      setScanning(false);
    }
  };

  if (!active) return null;

  const countMatch = String(reason || '').match(/:(\d+)/);
  const countHint = countMatch ? countMatch[1] : null;

  return (
    <div className="inventory-ledger-safe-mode-banner" role="alert">
      <div className="inventory-ledger-safe-mode-banner__inner">
        <AlertTriangle size={18} aria-hidden />
        <span>
          {ti('label')}
          {countHint ? ` (${countHint})` : ''}
        </span>
        {summary && !summary.ok && summary.error ? (
          <span className="inventory-ledger-safe-mode-banner__reason">
            {summary.error}
          </span>
        ) : null}
        <button
          type="button"
          className="inventory-ledger-safe-mode-banner__action"
          onClick={handleRescan}
          disabled={scanning}
        >
          <Activity size={14} aria-hidden />
          {scanning ? ti('scanning') : ti('rescan')}
        </button>
        {process.env.NODE_ENV === 'development' ? (
          <button
            type="button"
            className="inventory-ledger-safe-mode-banner__dismiss"
            onClick={() => disableStorageSafeMode()}
          >
            {ti('dismissDev')}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default InventoryLedgerSafeModeBanner;
